import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getItemsOfAllTypes} from "@/services/itemService.ts";
import {RequestOverview} from "@/types/RequestOverview.ts";
import {ToolbarOptions} from "@/types/data-table/ToolbarOptions.ts";
import {useMemo, useState} from "react";
import {getRequestsAvailabilitiesColumns} from "@/pages/dashboard/admin/requests-availabilities-columns.tsx";
import {DataTable} from "@/components/data-table/data-table.tsx";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {SectionTitle} from "@/components/ui/headings";
import {Button} from "@/components/ui/button";
import {LayoutGrid, RefreshCw, Table2} from "lucide-react";
import {useTranslation} from "react-i18next";
import {getCriteriaValue, keyValueMapping} from "@/utils/criteria-helper.tsx";
import {backfillGapSummary, getTrendSummary} from "@/services/suggestionService.ts";
import {SeasonTrendSummary} from "@/types/TrendSummary.ts";
import {cn} from "@/lib/utils";

/* ─── 1. Four number cards (top section) ─── */

interface AdminStatCardsProps {
    summary: RequestOverview;
}

export const AdminStatCards = ({summary}: AdminStatCardsProps) => {
    const {t} = useTranslation();

    return (
        <div className="p-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Mentors</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-xl font-semibold text-text-1">{summary.mentorCount}</div>
                    <p className="text-xs text-muted-foreground"></p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{t("navbar.mentees")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-xl font-semibold text-text-1">{summary.menteeCount}</div>
                    <p className="text-xs text-muted-foreground"></p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{t("dashboard.preferenceSubmitted")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-xl font-semibold text-text-1">{summary.preferenceCount}</div>
                    <p className="text-xs text-muted-foreground"></p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{t("dashboard.submissionPercentage")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div
                        className="text-xl font-semibold text-text-1">{((summary.preferenceCount / summary.menteeCount) * 100).toFixed(2)}%
                    </div>
                    <p className="text-xs text-muted-foreground">{t("dashboard.submissionDesc")}</p>
                </CardContent>
            </Card>
        </div>
    );
};

/* ─── 2. Gap highlights + gap by criteria visual ─── */

type GapViewMode = "card" | "table";

interface AdminGapSectionProps {
    summary: RequestOverview;
    seasonId?: number;
}

export const AdminGapSection = ({summary, seasonId}: AdminGapSectionProps) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [gapViewMode, setGapViewMode] = useState<GapViewMode>("card");

    const backfillMutation = useMutation({
        mutationFn: () => backfillGapSummary(seasonId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.fetchSummary.gaps });
            queryClient.invalidateQueries({ queryKey: queryKeys.fetchSummary.trends });
        },
    });

    const {data: criteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    const toolbarOptions: ToolbarOptions = {
        filters: [
            { column: "criteria", title: "criteria" },
            { column: "value", title: "value" },
        ],
        searchableColumn: "criteria",
    };

    const columns = useMemo(() => {
        if (!criteria) return [];
        return getRequestsAvailabilitiesColumns(criteria, t);
    }, [criteria, t]);

    const visualGroups = useMemo(() => {
        if (!criteria) return [];
        const groups = new Map<string, RequestOverview["requestsAndAvailabilities"]>();
        for (const row of summary.requestsAndAvailabilities) {
            const arr = groups.get(row.key) ?? [];
            arr.push(row);
            groups.set(row.key, arr);
        }
        return Array.from(groups.entries()).map(([key, rows]) => {
            const mapping = keyValueMapping[key];
            const label = mapping ? t(`criteria.${mapping.label}`) : key;
            const items = rows
                .map((row) => ({
                    ...row,
                    valueLabel: getCriteriaValue(criteria, row.key, row.value),
                }))
                .sort((a, b) => String(a.valueLabel).localeCompare(String(b.valueLabel)));
            return { key, label, items };
        });
    }, [criteria, summary.requestsAndAvailabilities, t]);

    const gapHighlights = useMemo(() => {
        if (!criteria) return null;
        const items = summary.requestsAndAvailabilities.map((row) => ({
            ...row,
            valueLabel: getCriteriaValue(criteria, row.key, row.value),
            criterionLabel: (() => {
                const mapping = keyValueMapping[row.key];
                return mapping ? t(`criteria.${mapping.label}`) : row.key;
            })(),
            gap: row.mentorPercentage - row.requestPercentage,
        }));
        const unused = items.filter((i) => i.requests === 0 && i.mentors === 0);
        const demandGap = items.filter((i) => i.requestPercentage > i.mentorPercentage && !(i.requests === 0 && i.mentors === 0));
        const surplus = items.filter((i) => i.mentorPercentage > i.requestPercentage && !(i.requests === 0 && i.mentors === 0));

        const topDemand = [...demandGap].sort((a, b) => (a.gap - b.gap)).slice(0, 5);
        const topSurplus = [...surplus].sort((a, b) => (b.gap - a.gap)).slice(0, 5);
        const topUnused = unused.slice(0, 5);

        return {
            counts: { demandGap: demandGap.length, surplus: surplus.length, unused: unused.length },
            topDemand,
            topSurplus,
            topUnused,
        };
    }, [criteria, summary.requestsAndAvailabilities]);

    if (!criteria) return null;

    return (
        <div className="p-6 pt-0 space-y-4">
            {gapHighlights && (
                <Card>
                    <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <CardTitle>{t("dashboard.gapHighlightsTitle")}</CardTitle>
                                <CardDescription className="text-base">
                                    {t("dashboard.gapHighlightsDesc")}
                                </CardDescription>
                            </div>
                            {seasonId != null && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => backfillMutation.mutate()}
                                    disabled={backfillMutation.isPending}
                                >
                                    <RefreshCw className={cn("size-4 shrink-0", backfillMutation.isPending && "animate-spin")} />
                                    <span className="ml-2">{t("dashboard.gapHighlightsRefresh")}</span>
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex flex-wrap gap-3 text-xs">
                            <span className="inline-flex items-center gap-2 rounded-full bg-tangerine/20 px-3 py-1 text-tangerine">
                                {t("dashboard.gapLegendDemand")}: {gapHighlights.counts.demandGap}
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full bg-springGreen/20 px-3 py-1 text-springGreen">
                                {t("dashboard.gapHighlightsSurplus")}: {gapHighlights.counts.surplus}
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full bg-ash px-3 py-1 text-slate">
                                {t("dashboard.gapLegendUnused")}: {gapHighlights.counts.unused}
                            </span>
                        </div>
                        <div className="grid gap-4 lg:grid-cols-3">
                            <div className="rounded-lg border p-3">
                                <h5 className="text-sm font-semibold font-heading uppercase text-tangerine">{t("dashboard.gapHighlightsDemand")}</h5>
                                <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
                                    {gapHighlights.topDemand.length === 0 ? (
                                        <li>{t("dashboard.gapHighlightsNone")}</li>
                                    ) : (
                                        gapHighlights.topDemand.map((item) => (
                                            <li key={`${item.key}:${item.value}`}>
                                                <span className="font-medium text-foreground">{item.criterionLabel} - {item.valueLabel}</span>{" "}
                                                <span>({item.requestPercentage}% vs {item.mentorPercentage}%)</span>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                            <div className="rounded-lg border p-3">
                                <h5 className="text-sm font-semibold font-heading uppercase text-springGreen">{t("dashboard.gapHighlightsSurplus")}</h5>
                                <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
                                    {gapHighlights.topSurplus.length === 0 ? (
                                        <li>{t("dashboard.gapHighlightsNone")}</li>
                                    ) : (
                                        gapHighlights.topSurplus.map((item) => (
                                            <li key={`${item.key}:${item.value}`}>
                                                <span className="font-medium text-foreground">{item.criterionLabel} - {item.valueLabel}</span>{" "}
                                                <span>({item.requestPercentage}% vs {item.mentorPercentage}%)</span>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                            <div className="rounded-lg border p-3">
                                <h5 className="text-sm font-semibold font-heading uppercase text-slate">{t("dashboard.gapHighlightsUnused")}</h5>
                                <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
                                    {gapHighlights.topUnused.length === 0 ? (
                                        <li>{t("dashboard.gapHighlightsNone")}</li>
                                    ) : (
                                        gapHighlights.topUnused.map((item) => (
                                            <li key={`${item.key}:${item.value}`}>
                                                <span className="font-medium text-foreground">{item.criterionLabel} - {item.valueLabel}</span>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <SectionTitle>{t("dashboard.gapVisualTitle")}</SectionTitle>
                    <div className="flex items-center gap-1 rounded-md border border-border p-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn("h-8 w-8", gapViewMode === "card" && "bg-accent/15 text-accent")}
                            onClick={() => setGapViewMode("card")}
                            aria-label={t("dashboard.gapViewCard")}
                            aria-pressed={gapViewMode === "card"}
                        >
                            <LayoutGrid className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn("h-8 w-8", gapViewMode === "table" && "bg-accent/15 text-accent")}
                            onClick={() => setGapViewMode("table")}
                            aria-label={t("dashboard.gapViewTable")}
                            aria-pressed={gapViewMode === "table"}
                        >
                            <Table2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground">{t("dashboard.gapVisualSubtitle")}</p>
            </div>

            {gapViewMode === "card" ? (
                <Card>
                    <CardContent className="pt-6 space-y-4">
                        <p className="text-sm text-muted-foreground">{t("dashboard.gapVisualDesc")}</p>
                        <div className="flex flex-wrap gap-3 text-xs">
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-tangerine" />
                                {t("dashboard.gapLegendDemand")}
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-springGreen" />
                                {t("dashboard.gapLegendBalanced")}
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-ash" />
                                {t("dashboard.gapLegendUnused")}
                            </span>
                        </div>
                        <div className="grid gap-4 lg:grid-cols-2">
                            {visualGroups.map((group) => (
                                <div key={group.key} className="rounded-lg border p-4">
                                    <h4 className="text-sm font-semibold">{group.label}</h4>
                                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                        {group.items.map((item) => {
                                            const isUnused = item.requests === 0 && item.mentors === 0;
                                            const isGap = item.requestPercentage > item.mentorPercentage;
                                            const bg = isUnused
                                                ? "bg-ash text-slate"
                                                : isGap
                                                    ? "bg-tangerine/20 text-tangerine"
                                                    : "bg-springGreen/20 text-springGreen";
                                            return (
                                                <div key={`${item.key}:${item.value}`} className={`rounded-md px-3 py-2 ${bg}`}>
                                                    <div className="text-sm font-medium">{item.valueLabel}</div>
                                                    <div className="text-xs opacity-80">
                                                        R: {item.requests} ({item.requestPercentage}%) • M: {item.mentors} ({item.mentorPercentage}%)
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="pt-6">
                        <DataTable
                            data={summary.requestsAndAvailabilities}
                            columns={columns}
                            id="summary"
                            toolbarOptions={toolbarOptions}
                        />
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

/* ─── 3. Numbers across seasons (trend) ─── */

export const AdminTrendSection = () => {
    const {t} = useTranslation();

    const {data: trendSummary} = useQuery({
        queryKey: queryKeys.fetchSummary.trends,
        queryFn: () => getTrendSummary(),
        initialData: [],
    });

    if (!trendSummary || trendSummary.length === 0) return null;

    return (
        <div className="p-6 pt-0">
            <Card>
                <CardHeader>
                    <CardTitle>{t("dashboard.trendTitle")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 min-w-0">
                        {trendSummary.map((row: SeasonTrendSummary, idx: number) => {
                            const prev = idx > 0 ? trendSummary[idx - 1] : null;
                            const delta = (current: number, previous?: number) => {
                                if (previous == null) return null;
                                const diff = current - previous;
                                if (diff === 0) return { text: "±0", diff: 0 };
                                return { text: `${diff > 0 ? "+" : ""}${diff}`, diff };
                            };
                            const metrics = [
                                { label: t("dashboard.trendMentors"), value: row.mentorCount, delta: prev ? delta(row.mentorCount, prev.mentorCount) : null },
                                { label: t("dashboard.trendMentees"), value: row.menteeCount, delta: prev ? delta(row.menteeCount, prev.menteeCount) : null },
                                { label: t("dashboard.trendPreferences"), value: row.preferenceCount, delta: prev ? delta(row.preferenceCount, prev.preferenceCount) : null },
                                { label: t("dashboard.trendMentorshipGroups"), value: row.mentorshipGroupsCount ?? 0, delta: prev ? delta(row.mentorshipGroupsCount ?? 0, prev.mentorshipGroupsCount ?? 0) : null },
                                { label: t("dashboard.trendGapCount"), value: row.demandGapCount, delta: prev ? delta(row.demandGapCount, prev.demandGapCount) : null, variant: "demand" as const },
                                { label: t("dashboard.trendSurplus"), value: row.surplusCount, delta: prev ? delta(row.surplusCount, prev.surplusCount) : null, variant: "surplus" as const },
                                { label: t("dashboard.trendUnused"), value: row.unusedCount, delta: prev ? delta(row.unusedCount, prev.unusedCount) : null },
                                { label: t("dashboard.trendDiversity"), value: row.mentorDiversityCount, delta: prev ? delta(row.mentorDiversityCount, prev.mentorDiversityCount) : null },
                            ];
                            return (
                                <div
                                    key={row.seasonId}
                                    className="rounded-xl border border-border bg-bg-elevated/40 p-4 space-y-3"
                                >
                                    <div className="pb-2 border-b border-border">
                                        <div className="text-base font-semibold text-text-1">{row.seasonName}</div>
                                        {row.startDate && (
                                            <div className="text-xs text-muted-foreground mt-0.5">{row.startDate}</div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {metrics.map((m) => (
                                            <div key={m.label} className="min-w-0">
                                                <div className="text-xs text-muted-foreground truncate" title={m.label}>{m.label}</div>
                                                <div className="flex items-baseline gap-2 flex-wrap">
                                                    <span className="text-base font-semibold text-text-1">{m.value}</span>
                                                    {m.delta && (
                                                        <span
                                                            className={cn(
                                                                "text-xs font-medium shrink-0",
                                                                m.variant === "demand" && m.delta.diff > 0 && "text-tangerine",
                                                                m.variant === "surplus" && m.delta.diff > 0 && "text-springGreen",
                                                                !m.variant && m.delta.diff !== 0 && "text-muted-foreground",
                                                                m.delta.diff === 0 && "text-muted-foreground"
                                                            )}
                                                        >
                                                            {m.delta.text}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

/* ─── 4. Composite (backward-compatible) ─── */

interface AdminInsightsPageProps {
    summary: RequestOverview;
    seasonId?: number;
}

export const AdminInsightsPage = ({summary, seasonId}: AdminInsightsPageProps) => {
    return (
        <>
            <AdminStatCards summary={summary} />
            <AdminGapSection summary={summary} seasonId={seasonId} />
            <AdminTrendSection />
        </>
    );
};
